/// <amd-module name="Feed/CreateFeed/Components/CreateFeedPanel" />
import * as React from "react";
import { connect } from "react-redux";
import { DynamicModuleLoader } from "redux-dynamic-modules";

import * as Utils_String from "VSS/Core/Util/String";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { getVssPageData } from "VSS/Redux/Modules/VSSPageData";

import { Checkbox } from "VSSUI/Checkbox";
import { Icon } from "VSSUI/Icon";
import { Link } from "VSSUI/Link";
import { MessageBar, MessageBarSeverity } from "VSSUI/MessageBar";
import { Panel } from "VSSUI/Panel";
import { RadioButton, RadioButtonGroup } from "VSSUI/RadioButton";
import { Spinner } from "VSSUI/Spinner";
import { TextFieldWithMessage } from "VSSUI/TextField";
import { getSafeId } from "VSSUI/Util";

import * as Resources from "Feed/Common/Resources";
import { IFeedWebPageDataService } from "Feed/Common/Services/IFeedWebPageDataService";
import { ValidateFeedName } from "Feed/Common/Utils/Validator";
import { FeedHubActions } from "Feed/Core/Redux/FeedHub/FeedHubActions";
import { CreateFeedActions } from "Feed/CreateFeed/Redux/CreateFeed/CreateFeedActions";
import { ICreateFeedAwareState, Visibility } from "Feed/CreateFeed/Redux/CreateFeed/CreateFeedContracts";
import { CreateFeedModule } from "Feed/CreateFeed/Redux/CreateFeed/CreateFeedModule";
import { ICreateFeedData } from "Feed/CreateFeed/Types";
import { ProjectVisibility } from "Tfs/Platform/Contracts/Core";
import { IProjectData } from "Tfs/Platform/Page";
import { currentProject } from "Tfs/ReduxCommon/Modules/TfsPageData/TfsPageData";

const Actions = {
    saveChanges: CreateFeedActions.createFeed,
    setError: CreateFeedActions.setError,
    hidePanel: FeedHubActions.hideCreateFeed
};

interface ICreateFeedPanelProps {
    collectionName: string;
    errorMessage?: string;
    isPublicProject: boolean;
    isSaving: boolean;
    project: IProjectData;
}

interface ICreateFeedPanelPanelState {
    feedName: string;
    scope: string;
    isInvalidFeedName: boolean;
    selectedVisibilityId: string;
    upstreamsChecked: boolean;
    isButtonDisabled: boolean;
}

const ScopeProj = "project";
const ScopeOrg = "org";

class CreateFeedPanel extends VssComponent<ICreateFeedPanelProps & typeof Actions, ICreateFeedPanelPanelState> {
    private aadUpstreamEnabled: boolean;

    constructor(props: ICreateFeedPanelProps & typeof Actions, context?: IVssComponentContext) {
        super(props, context);
        this.initialize();
    }

    private initialize() {
        const pageContext = this.context.pageContext;

        let visibility = Visibility.public;
        if (!this.props.isPublicProject) {
            const webPageDataService = pageContext.getService<IFeedWebPageDataService>("IFeedWebPageDataService");
            this.aadUpstreamEnabled = webPageDataService.isOrgAadBacked();
            visibility = this.aadUpstreamEnabled ? Visibility.aadTenant : Visibility.collection;
        }

        this.state = {
            isInvalidFeedName: false,
            feedName: "",
            scope: ScopeProj,
            upstreamsChecked: !this.props.isPublicProject,
            selectedVisibilityId: visibility,
            isButtonDisabled: true
        };
    }

    public render(): JSX.Element {
        const { isSaving } = this.props;
        const { isButtonDisabled } = this.state;
        const panelTitleId = "create-feed-panel-title";
        return (
            <Panel
                className="create-feed-panel"
                titleProps={{
                    text: Resources.CreateFeed_PageTitle,
                    id: panelTitleId // getSafeId call is done in the panel component
                }}
                description={Resources.CreateFeed_Description}
                onDismiss={this.hideCreateFeedPanel}
                footerButtonProps={[
                    { text: Resources.Dialog_CancelButtonText, onClick: this.hideCreateFeedPanel },
                    {
                        text: Resources.CreateFeed_Create,
                        primary: true,
                        onClick: this.createFeed,
                        disabled: isButtonDisabled,
                        type: "submit"
                    }
                ]}
                defaultActiveElement="input"
                overlayContent={isSaving ? <Spinner label={Resources.Overlay_Saving} /> : undefined}
            >
                {this.getPanelContent(panelTitleId)}
            </Panel>
        );
    }

    private getPanelContent(panelTitleId: string): JSX.Element {
        const { isPublicProject, errorMessage, project } = this.props;
        const { isInvalidFeedName, feedName } = this.state;
        const textFieldAriaDescribedBy = "feedname-instructions";

        return (
            <form
                aria-labelledby={getSafeId(panelTitleId)}
                className="create-feed-panel-content"
                onSubmit={event => {
                    this.createFeed();
                    // Prevent the page from reloading on form submission
                    event.preventDefault();
                }}
            >
                {errorMessage && (
                    <MessageBar
                        className="global-settings-error-message"
                        severity={MessageBarSeverity.Error}
                        onDismiss={this.dismissError}
                    >
                        {errorMessage}
                    </MessageBar>
                )}
                <fieldset>
                    <TextFieldWithMessage
                        className="create-feed-section"
                        message={
                            isInvalidFeedName
                                ? Resources.CreateFeed_FeedNameError
                                : Resources.CreateFeed_FeedNameHelpMessage
                        }
                        textFieldProps={{
                            label: Resources.CreateFeed_Name,
                            required: true,
                            value: feedName,
                            maxLength: 64,
                            onChange: this.onFeedNameChange,
                            ariaDescribedBy: textFieldAriaDescribedBy
                        }}
                        error={isInvalidFeedName}
                    />
                    <span className="visibly-hidden" id={getSafeId(textFieldAriaDescribedBy)}>
                        {
                            // This message is only for the ariaDescribedBy value of the feed name textfield.
                            Resources.CreateFeed_FeedNameHelpMessage + ". " + Resources.CreateFeed_FeedNameError
                        }
                    </span>
                </fieldset>

                {isPublicProject ? this.getPublicProjectContent() : this.getPrivateProjectContent()}
                {this.renderScope(project.name)}
            </form>
        );
    }

    private renderScope(projectName: string): JSX.Element {
        return (
            <fieldset>
                <legend className="title-xs">{Resources.Scope}</legend>

                <RadioButtonGroup
                    selectedButtonId={this.state.scope}
                    onSelect={buttonId => this.setState({ scope: buttonId })}
                >
                    <RadioButton id={ScopeProj}>
                        <div className="bolt-radio-button-label" id={getSafeId("scope-project")}>
                            {Utils_String.format(Resources.CreateFeed_ProjectHeading, projectName)}
                            <span id={getSafeId("scope-project" + "-helptext")}>
                                <FormatComponent
                                    className="create-feed-section create-feed-helptext"
                                    format={Resources.CreateFeed_ProjectScopedDescription}
                                >
                                    {projectName}
                                    <Link
                                        href={Resources.CreateFeed_ProjectScopedFeed_Fwlink}
                                        target="_blank"
                                        tabIndex={0}
                                        rel="nofollow noreferrer noopener help"
                                        onClick={ev => ev.stopPropagation()}
                                        underline
                                    >
                                        {Resources.LearnMore}
                                    </Link>
                                </FormatComponent>
                            </span>
                        </div>
                    </RadioButton>
                    <RadioButton id={ScopeOrg} text={Resources.Organization} />
                </RadioButtonGroup>
            </fieldset>
        );
    }

    private getPublicProjectContent(): JSX.Element {
        return (
            <fieldset>
                <legend className="title-xs">{Resources.CreateFeed_Visibility}</legend>
                <div>
                    <Icon ariaLabel={Resources.AriaLabel_GlobeIcon} iconName="Globe" className="create-feed-globe" />
                    <span>{Resources.Public}</span>
                </div>
                <p className="create-feed-helptext">{Resources.CreateFeed_PublicProjectDescription}</p>
            </fieldset>
        );
    }

    private getPrivateProjectContent(): JSX.Element {
        return (
            <React.Fragment>
                <fieldset key="visibility">
                    <legend className="title-xs">{Resources.CreateFeed_Visibility}</legend>
                    <RadioButtonGroup
                        className="fontSizeM create-feed-section"
                        selectedButtonId={this.state.selectedVisibilityId}
                        onSelect={(selectedButtonId: string) => this.onVisibilityChange(selectedButtonId)}
                    >
                        {this.getPrivateVisibilityRadioOptions()}
                    </RadioButtonGroup>
                </fieldset>
                <fieldset key="upstream">
                    <legend className="title-xs">{Resources.CreateFeed_UpstreamSources}</legend>
                    <Checkbox
                        label={Resources.CreateFeed_IncludeUpstreams}
                        checked={this.state.upstreamsChecked}
                        onChange={(event, checked) => this.onCheckboxChange(checked)}
                        ariaDescribedBy="create-feed-upstream-helptext"
                    />
                    <p id={getSafeId("create-feed-upstream-helptext")} className="create-feed-helptext">
                        {Resources.CreateFeed_UpstreamExample}
                    </p>
                </fieldset>
            </React.Fragment>
        );
    }

    private getPrivateVisibilityRadioOptions(): JSX.Element[] {
        const options: JSX.Element[] = [
            this.getVisibilityOptionElement(
                Visibility.collection,
                "collection-visibility-radio-button-label",
                Utils_String.format(Resources.CreateFeed_MembersOf_Header, this.props.collectionName),
                Utils_String.format(
                    Resources.CreateFeed_MemberVisibility_Description,
                    Resources.CreateFeed_MemberVisibility_Description_Organization
                )
            ),
            // Showing project visibility in a follow-up PR
            /*this.getVisibilityOptionElement(
                Visibility.project,
                "project-visibility-radio-button-label",
                Utils_String.format(
                    Resources.CreateFeed_MembersOf_Header,
                    this.props.project.name
                ),
                Utils_String.format(
                    Resources.CreateFeed_MemberVisibility_Description,
                    Resources.CreateFeed_MemberVisibility_Description_Project
                )),*/
            this.getVisibilityOptionElement(
                Visibility.private,
                "private-visibility-radio-button-label",
                Resources.CreateFeed_PrivateVisibility_Header,
                Resources.CreateFeed_PrivateVisibility_Description
            )
        ];

        if (this.aadUpstreamEnabled) {
            // Add to the top of the list
            options.unshift(
                this.getVisibilityOptionElement(
                    Visibility.aadTenant,
                    "aad-tenant-visibility-radio-button-label",
                    Utils_String.format(
                        Resources.CreateFeed_MembersOf_Header,
                        Resources.CreateFeed_AadTenantVisibility
                    ),
                    Resources.CreateFeed_AadTenantVisibility_Description
                )
            );
        }

        return options;
    }

    private getVisibilityOptionElement(
        visibility: string,
        elementId: string,
        header: string,
        description: string
    ): JSX.Element {
        return (
            <RadioButton
                key={visibility}
                id={visibility}
                ariaLabelledBy={elementId}
                aria-describedBy={elementId + "-helptext"}
            >
                <div className="bolt-radio-button-label" id={getSafeId(elementId)}>
                    {header}
                    <span className="create-feed-helptext" id={getSafeId(elementId + "-helptext")}>
                        {description}
                    </span>
                </div>
            </RadioButton>
        );
    }

    private onFeedNameChange = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | undefined,
        newValue: string
    ): void => {
        const isValid = ValidateFeedName(newValue);

        this.setState({
            feedName: newValue,
            isInvalidFeedName: !isValid,
            isButtonDisabled: !isValid || newValue == ""
        });
    };

    private onVisibilityChange(selectedButtonId: string) {
        this.setState({ selectedVisibilityId: selectedButtonId });
        this.dismissError();
    }

    private onCheckboxChange(checked: boolean) {
        this.setState({ upstreamsChecked: checked });
        this.dismissError();
    }

    private dismissError = (): void => {
        this.props.setError(undefined);
    };

    private hideCreateFeedPanel = (): void => {
        this.props.hidePanel();
    };

    private createFeed = (): void => {
        const createFeedData: ICreateFeedData = {
            feedName: this.state.feedName,
            isProjectScoped: this.state.scope === ScopeProj,
            upstreamSources: this.state.upstreamsChecked,
            visibility: this.state.selectedVisibilityId
        };
        if (!this.state.isButtonDisabled) {
            this.props.saveChanges(createFeedData);
        }
    };
}

function mapStateToProps(state: ICreateFeedAwareState): ICreateFeedPanelProps {
    const createFeedState = state.createFeedState;
    const project = currentProject(state)!;

    return {
        collectionName: getVssPageData(state)!.hostName,
        errorMessage: createFeedState.errorMessage,
        isPublicProject: project.visibility === ProjectVisibility.Public,
        isSaving: createFeedState.isSaving,
        project
    };
}

const ConnectedCreateFeedPanel = connect(mapStateToProps, Actions)(CreateFeedPanel);

class DynamicCreateFeedPanel extends React.PureComponent {
    public render() {
        return (
            <DynamicModuleLoader modules={CreateFeedModule}>
                <ConnectedCreateFeedPanel />
            </DynamicModuleLoader>
        );
    }
}

VssComponent.register("ms.feed.feed-createfeed.create-feed-panel", DynamicCreateFeedPanel);
